/**
 * Centered play/pause toggle for A/B extra-media videos (CFSN-579/580).
 *
 * Scoped to <deferred-media data-em-video> (gallery slide + size-chart modal).
 * The toggle markup is server-rendered and CSS-hidden until the video loads;
 * this script wires its click to the <video> and mirrors play/pause state via
 * the `is-playing` class on the deferred-media.
 */
(function () {
  const wired = new WeakSet();

  function wire(dm, video) {
    if (wired.has(video)) return;
    wired.add(video);

    const toggle = dm.querySelector('[data-em-toggle]');
    if (!toggle) return;

    const playLabel = toggle.dataset.playLabel || '';
    const pauseLabel = toggle.dataset.pauseLabel || '';

    const sync = () => {
      const playing = !video.paused && !video.ended;
      if (dm.classList.contains('is-playing') !== playing) {
        dm.classList.toggle('is-playing', playing);
        toggle.setAttribute('aria-label', playing ? pauseLabel : playLabel);
      }
    };

    const togglePlay = () => {
      if (video.paused) {
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } else {
        video.pause();
      }
    };

    // Re-wiring injects a fresh <video>; replace the toggle handler instead of
    // stacking a new one each time (old handlers captured detached videos).
    if (toggle._emClickHandler) toggle.removeEventListener('click', toggle._emClickHandler);
    toggle._emClickHandler = (e) => {
      e.preventDefault();
      togglePlay();
    };
    toggle.addEventListener('click', toggle._emClickHandler);

    // Click anywhere on the video toggles play/pause too (not just the icon).
    if (video._emClickHandler) video.removeEventListener('click', video._emClickHandler);
    video._emClickHandler = () => togglePlay();
    video.addEventListener('click', video._emClickHandler);

    // `play`/`pause` can be missed when autoplay fires before this wires up;
    // `timeupdate` runs continuously during playback and self-corrects state.
    video.addEventListener('play', sync);
    video.addEventListener('playing', sync);
    video.addEventListener('timeupdate', sync);
    video.addEventListener('pause', sync);
    video.addEventListener('ended', sync);
    sync();

    // Pause when the video scrolls out of the viewport; resume when it scrolls
    // back in — but only if the scroll-out paused it (a manual pause stays paused).
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              if (!video.paused) {
                video.pause();
                video._emPausedByViewport = true;
              }
            } else if (video._emPausedByViewport) {
              video._emPausedByViewport = false;
              const resumed = video.play();
              if (resumed && typeof resumed.catch === 'function') resumed.catch(() => {});
            }
          });
        },
        { threshold: 0 }
      );
      io.observe(video);
      video._emIO = io; // kept so the slide-change reset can disconnect it
    }

    // Mute/unmute toggle (bottom-right). Native controls are removed, so this is
    // the only sound control; videos autoplay muted and the user unmutes here.
    const volume = dm.querySelector('[data-em-volume]');
    if (volume) {
      const muteLabel = volume.dataset.muteLabel || '';
      const unmuteLabel = volume.dataset.unmuteLabel || '';
      const syncVolume = () => {
        dm.classList.toggle('is-muted', video.muted);
        volume.setAttribute('aria-label', video.muted ? unmuteLabel : muteLabel);
      };
      if (volume._emClickHandler) volume.removeEventListener('click', volume._emClickHandler);
      volume._emClickHandler = (e) => {
        e.preventDefault();
        video.muted = !video.muted;
        video._emAutoMuted = false; // user took manual control of sound
        syncVolume();
      };
      volume.addEventListener('click', volume._emClickHandler);
      video.addEventListener('volumechange', syncVolume);
      syncVolume();
    }
  }

  function observe(dm) {
    if (dm._emObserved) return;
    dm._emObserved = true;

    const tryWire = () => {
      const video = dm.querySelector('video');
      if (video) wire(dm, video); // wire() guards against double-wiring via WeakSet
    };
    tryWire(); // in case a video is already present
    // Keep observing: the slide-change reset removes the <video>, so a fresh one
    // is injected on the next play and must be re-wired.
    new MutationObserver(tryWire).observe(dm, { childList: true, subtree: true });
  }

  function init() {
    document.querySelectorAll('deferred-media[data-em-video]').forEach(observe);
    // Gallery rebuilds (e.g. per-color variant change) replace gallery HTML and
    // inject fresh deferred-media nodes — observe them as they appear.
    new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('deferred-media[data-em-video]')) observe(node);
          if (node.querySelectorAll) {
            node.querySelectorAll('deferred-media[data-em-video]').forEach(observe);
          }
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
